import { useState, useCallback, MutableRef, useRef, useEffect } from 'preact/hooks';
import {
  keyExtractor,
  getFirstVisibleKey,
  getKey,
  getPrevNextKeyUsingRef
} from '../utils/PRIVATE_collectionUtils';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useCollectionFocusRing } from '../hooks/PRIVATE_useCollectionFocusRing';
import { useCurrentKey } from '../hooks/PRIVATE_useCurrentKey';
import { NavigationListContextProps } from '../UNSAFE_NavigationListCommon';

import { ComponentChildren } from 'preact';

/**
 * type for payload of current key change event handler
 */
type CurrentKeyDetail<K> = {
  value: K;
};
/**
 * type for payload of selection change event handler
 */
type SelectionDetail<K> = {
  value: K;
  reason: 'pointer' | 'keyboard';
};
/**
 * type for payload of remove event handler
 */
type RemoveDetail<K> = {
  value: K;
};

/*TODO: JET-58534. Create a hook to share some logic between navlist and tabbar. Hook will be used inside this one. */
export function useNavigationList<K extends string | number>({
  containerRef,
  selection,
  onRemove,
  onSelectionChange,
  children
}: {
  containerRef: MutableRef<HTMLUListElement>;
  selection?: K;
  onRemove?: <K extends string | number>(detail: RemoveDetail<K>) => void;
  onSelectionChange?: <K extends string | number>(detail: SelectionDetail<K>) => void;
  children: ComponentChildren;
}) {
  const ITEM_SELECTOR = '[role="option"]';
  const REMOVAL_ICON_SELECTOR = '[data-oj-navigationlist-item-remove-icon]';
  const [currentKey, setCurrentKey] = useState<K | undefined>(selection);

  const onCurrentKeyChange = useCallback((detail: CurrentKeyDetail<K | undefined>) => {
    setCurrentKey(detail.value);
  }, []) as NavigationListContextProps<K>['onCurrentKeyChange'];

  const { currentKeyProps } = useCurrentKey(
    (element) =>
      onRemove
        ? (extractOnlyItemKey(element, ITEM_SELECTOR, REMOVAL_ICON_SELECTOR) as K)
        : keyExtractor(element, ITEM_SELECTOR),
    false,
    getPrevNextKeyUsingRef(containerRef, currentKey, true, ITEM_SELECTOR),
    getPrevNextKeyUsingRef(containerRef, currentKey, false, ITEM_SELECTOR),
    undefined,
    undefined,
    currentKey,
    onCurrentKeyChange
  );

  const [showFocusRing, focusRingProps] = useCollectionFocusRing(containerRef, [
    'Home',
    'End',
    'ArrowUp',
    'ArrowDown'
  ]);

  const onFocus = useCallback(() => {
    if (containerRef.current && currentKey === undefined) {
      const key = getFirstVisibleKey(containerRef.current, ITEM_SELECTOR);
      if (key) {
        setCurrentKey(key as K);
      }
    }
  }, [currentKey, containerRef]);

  const onFocusProps = { onFocus };
  const prevNavItems = useRef<K[]>();

  useEffect(() => {
    if (containerRef.current) {
      const navListItemKey = Array.from(
        containerRef.current.querySelectorAll(ITEM_SELECTOR),
        (elem) => getKey(elem as HTMLElement) as K
      );
      if (currentKey && navListItemKey.indexOf(currentKey) === -1 && prevNavItems.current) {
        setCurrentKey(findNextCurrentKey(currentKey, navListItemKey, prevNavItems.current));
      }
      prevNavItems.current = navListItemKey;
    }
  }, [children, currentKey, containerRef]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if ((event.key === 'Home' || event.key === 'End') && containerRef.current && currentKey) {
        const navListItemKey = Array.from(
          containerRef.current.querySelectorAll(ITEM_SELECTOR),
          (elem) => getKey(elem as HTMLElement) as K
        );
        onCurrentKeyChange?.({
          value: navListItemKey[event.key === 'Home' ? 0 : navListItemKey.length - 1] as K
        });
      }
      if (currentKey && event.key === 'Enter') {
        onSelectionChange?.({
          value: currentKey as K,
          reason: 'keyboard'
        });
      }
      if (currentKey && event.key === 'Delete') {
        onRemove?.({ value: currentKey as K });
      }
    },
    [currentKey, onSelectionChange, onRemove, onCurrentKeyChange, containerRef]
  );

  return {
    showFocusRing,
    currentKey,
    onCurrentKeyChange,
    onKeyDown,
    containerHandlers: mergeProps(focusRingProps, currentKeyProps, onFocusProps)
  };
}

const extractOnlyItemKey = (element: HTMLElement, itemSelector: string, itemEliminator: string) => {
  const navigationListItem = element.closest(itemSelector);
  const removeButton = element.closest(itemEliminator);
  return navigationListItem?.contains(removeButton) ? null : keyExtractor(element, itemSelector);
};

const findNextCurrentKey = <K>(currentKey: K, currNavs: K[], prevNavs: K[]) => {
  const index = prevNavs.indexOf(currentKey);
  if (index > 0) {
    const nextIndex = index === prevNavs.length - 1 ? index - 1 : index + 1;
    const nextKey = prevNavs[nextIndex];
    if (currNavs.indexOf(nextKey) !== -1) {
      return nextKey;
    }
  }
  // update current key to be the first one if we can't find a suitable next key
  return currNavs[0];
};
